import { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { SisMeaning } from './sisMatchingApi';
import { SIS, sisText } from './sisTokens';

type Props = {
  meanings: SisMeaning[];
  current?: string;
  disabled?: boolean;
  onPick: (meaning: string) => void;
};

export function MeaningMenu({ meanings, current, disabled, onPick }: Props) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement | null>(null);
  const label = meanings.find((m) => m.key === current)?.label ?? 'Choose a meaning';
  const empty = !current;
  const options: SisMeaning[] = empty ? meanings : [{ key: '', label: 'No meaning yet', hint: 'Back to choosing a meaning' }, ...meanings];

  useEffect(() => {
    if (!open) return undefined;
    const close = (event: MouseEvent | KeyboardEvent) => {
      if (event instanceof KeyboardEvent && event.key !== 'Escape') return;
      if (event instanceof MouseEvent && root.current?.contains(event.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);

  return (
    <Box ref={root} sx={{ position: 'relative', width: 280, flexShrink: 0 }}>
      <Box
        component="button"
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        data-empty={empty ? 'true' : 'false'}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        sx={{
          ...sisText.body1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          width: '100%',
          height: 40,
          padding: '0 8px 0 12px',
          border: `1px solid ${empty ? SIS.warning : SIS.divider}`,
          boxShadow: open ? `inset 0 0 0 1px ${SIS.primary}` : 'none',
          borderColor: open ? SIS.primary : undefined,
          borderRadius: '8px',
          bgcolor: SIS.surface,
          color: empty ? SIS.muted : SIS.ink,
          textAlign: 'left',
          cursor: disabled ? 'default' : 'pointer',
          '&:hover': { borderColor: open ? SIS.primary : SIS.muted },
        }}
      >
        <span>{label}</span>
        <ExpandMoreIcon aria-hidden="true" sx={{ width: 20, height: 20, color: SIS.muted }} />
      </Box>
      {open && (
        <Box
          role="listbox"
          sx={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 30, width: 320, bgcolor: SIS.surface, borderRadius: '8px', boxShadow: SIS.cardShadow, padding: '8px 0', maxHeight: 400, overflow: 'auto' }}
        >
          {options.map((m) => {
            const selected = m.key === current;
            const separated = m.key === 'ignore';
            return (
              <Box
                key={m.key}
                component="button"
                type="button"
                role="option"
                aria-selected={selected}
                data-sep={separated ? 'true' : undefined}
                onClick={() => {
                  setOpen(false);
                  onPick(m.key);
                }}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  width: '100%',
                  padding: '8px 16px',
                  border: 0,
                  background: selected ? SIS.headerBg : 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  boxShadow: separated ? `inset 0 1px 0 ${SIS.divider}` : 'none',
                  '&:hover': { bgcolor: SIS.hoverBg },
                }}
              >
                <Typography data-testid="sis-meaning-label" sx={{ ...sisText.body1, fontWeight: selected ? 700 : 400 }}>{m.label}</Typography>
                {m.hint && <Typography sx={{ ...sisText.body2, color: SIS.muted }}>{m.hint}</Typography>}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
